import clsx from "clsx"
import { forwardRef, type ReactNode } from "react"

import type { SX } from "../../theme"
import { AnimatedElement } from "../AnimatedElement"
import { Box } from "../Box"
import { Text } from "../Text"
import styles from "./FormField.module.css"
import { FormFieldError } from "./FormFieldError"

export interface FormFieldProps {
  children?: ReactNode
  error?: boolean | string
  description?: string
  descriptionPosition?: "top" | "bottom"
  sx?: SX
  id?: string
}

/**
 * A field with a description and optional error message.
 */
export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  function FormField(props, ref) {
    const {
      children,
      error = false,
      sx,
      description,
      descriptionPosition = "top",
      id,
    } = props

    return (
      <div ref={ref} className={styles.formField} style={sx}>
        {description && descriptionPosition === "top" && (
          <FormFieldDescription
            description={description}
            sx={{ marginBottom: 4 }}
          />
        )}

        {children}
        <AnimatedFormError error={error} id={id} />

        {description && descriptionPosition === "bottom" && (
          <FormFieldDescription
            description={description}
            sx={{ marginTop: 4 }}
          />
        )}
      </div>
    )
  },
)

type LabelProps = { label: string } | object

export type RoundedSide = "all" | "top" | "bottom"

export type FormInputFieldProps = {
  disabled?: boolean
  size?: "small" | "medium" | "large"
  roundedSide?: RoundedSide
  /**
   * Providing an id to both FormInputField and an Input field
   * allows to connect the input to an error message (via aria-errormessage).
   */
  id?: string
} & LabelProps &
  FormFieldProps

/**
 * A field with a label, description and optional error message.
 */
export const FormInputField = forwardRef<HTMLDivElement, FormInputFieldProps>(
  function FormInputField(props, ref) {
    const {
      children,
      disabled = false,
      error = false,
      sx,
      size = "large",
      description,
      descriptionPosition,
      roundedSide = "all",
      id,
    } = props

    const hasLabel = "label" in props && props.label !== undefined

    return (
      <div ref={ref} className={styles.formInputField} style={sx}>
        {hasLabel && (
          <Text
            variant="emphasized"
            component="label"
            htmlFor={props.id}
            sx={!description ? { marginBottom: 4 } : undefined}
          >
            {props.label}
          </Text>
        )}
        <FormField
          id={id}
          error={error}
          description={description}
          descriptionPosition={descriptionPosition}
        >
          <div
            className={clsx(
              styles.field,
              styles[`fieldSize-${size}`],
              styles[`fieldColor-${getState(disabled, error)}`],
              styles[`roundedSide-${roundedSide}`],
            )}
          >
            {children}
          </div>
        </FormField>
      </div>
    )
  },
)

interface FormFieldDescriptionProps {
  description: string
  sx?: SX
}

function FormFieldDescription(props: FormFieldDescriptionProps) {
  const { description, sx } = props
  return (
    <Text component="span" color="grey11" sx={sx}>
      {description}
    </Text>
  )
}

interface AnimatedFormErrorProps {
  error?: string | boolean
  id?: string
}

export function AnimatedFormError(props: AnimatedFormErrorProps) {
  const { error, id } = props

  return (
    <AnimatedElement animation={{ name: "expand-vertical" }}>
      {typeof error === "string" && (
        <Box flexDirection="column">
          <FormFieldError id={id}>{error}</FormFieldError>
        </Box>
      )}
    </AnimatedElement>
  )
}

function getState(disabled: boolean, error: FormFieldProps["error"]) {
  if (disabled) return "disabled"
  if (error) return "error"
  return "normal"
}
